// @flow
import type {
	FullStateLiteracyEventFeedbackRequest,
	FullStateLiteracyEventStudentTaskStateSpeaking,
	JuniorSharedStudent,
} from '@mission.io/mission-toolkit'
import type { MarkdownOverrideBundle as MarkdownCustomComponents } from '@mission.io/question-views'

import { getTeams } from '../../../store/selectors/jrPlusState/teams'
import { getStudents } from '../../../store/selectors/sharedSelectors'

import { getStudentId } from '../../../store/stores/general'
import Popover from '../../Controller/Popover.jsx'
import { LITERACY_EVENT } from '@mission.io/mission-toolkit/constants'

import isEqual from 'lodash/isEqual'
import React, { useMemo, Fragment } from 'react'
import { useSelector } from 'react-redux'

const STUDENT_NAME_FALLBACK = 'unknown student'
const TEAM_NAME_FALLBACK = 'unknown'

/**
 * getSpeakingTaskPreparser - get a markdown preparser to act on a string to make studentName (if allowed) and teamName (if allowed) renderable by the markdown parser.
 *
 * @param {Object} permissions - what should be made renderable
 * @param {boolean} permissions.allowStudentName - if true, will allow rendering the studentName component in markdown
 * @param {boolean} permissions.allowTeamName - if true, will allow rendering the teamName component in markdown
 *
 * @return {(string) => string} - a markdown perparser which will take in a string with custom component tokens and convert it to a string that markdown can process
 */
export function getSpeakingTaskPreparser(permissions: {
	allowStudentName: boolean,
	allowTeamName: boolean,
}): string => string {
	return (markdownText: string): string => {
		let convertedText = markdownText
		if (permissions.allowStudentName) {
			// the `g` flag causes all instances to be replaced
			convertedText = convertedText.replace(/{{student( |_){0,1}name}}/gi, '<studentName />')
		}
		if (permissions.allowTeamName) {
			// the `g` flag causes all instances to be replaced
			convertedText = convertedText.replace(/{{team( |_){0,1}name}}/gi, '<teamName />')
		}
		return convertedText
	}
}

type MarkdownOverridesBundle = {
	customComponents?: ?MarkdownCustomComponents,
	preparser?: ?(text: string) => string,
}

/**
 * useMarkdownOverridesForStudentIds - get overrides to populate the studentName and teamName placeholders in markdown text
 *
 * @param {Array<string>} studentIds - the ids of the students to populate the placeholders with
 *
 * @return {MarkdownOverridesBundle}
 */
export function useMarkdownOverridesForStudentIds(
	studentIds?: ?Array<string>
): MarkdownOverridesBundle {
	const students = useSelector(getStudents)

	const teams = useSelector(getTeams, isEqual)

	return useMemo(
		// memoized since this creates react components. If this is not memoized, react will keep rerendering the entire markdown since it is possible that the components in the tree have changed. This can lead to high cpu usage and pop-overs blinking.
		() => {
			const studentIdToTeamId = {}
			const teamIdToTeam = {}
			const teamIdToStudents = {}
			teams?.forEach(team => {
				team.students.forEach(student => {
					studentIdToTeamId[student.id] = team.id
					teamIdToStudents[team.id] ??= []
					teamIdToStudents[team.id].push(student)
				})
				const teamLead = team.lead
				if (teamLead) {
					studentIdToTeamId[teamLead.id] = team.id
					teamIdToStudents[teamLead.id] ??= []
					teamIdToStudents[teamLead.id].push(teamLead)
				}
				teamIdToTeam[team.id] = team
			})

			const studentNames = []
			const teamIdsToShow = new Set()

			studentIds?.forEach(studentId => {
				const studentData = students[studentId]
				if (studentData) {
					studentNames.push(`${studentData.firstName} ${studentData.lastName}`)
				}

				const studentTeamId = studentIdToTeamId[studentId]
				if (studentTeamId) {
					teamIdsToShow.add(studentTeamId)
				}
			})

			const teamNames = []
			const otherStudentsOnTeams: Array<{ teamName: string, students: Array<string> }> = []
			teamIdsToShow.forEach(teamId => {
				let teamName = teamIdToTeam[teamId]?.displayName
				if (!teamName) {
					return
				}
				teamNames.push(teamName)

				const studentsOnTeam = teamIdToStudents[teamId]
				if (!studentsOnTeam) {
					return
				}
				otherStudentsOnTeams.push({
					teamName,
					students: studentsOnTeam.map(
						(student: JuniorSharedStudent) => `${student.firstName} ${student.lastName}`
					),
				})
			})

			const studentName = studentNames.join(' and ') || STUDENT_NAME_FALLBACK
			const teamName = teamNames.join(' and ') || TEAM_NAME_FALLBACK

			return {
				customComponents: {
					studentName: {
						markdown: function StudentName() {
							return <b className="notranslate">{studentName}</b>
						},
						textToSpeech: () => ({ text: studentName }),
					},
					teamName: {
						markdown: function TeamName() {
							return (
								<Popover
									trigger={<b className="notranslate">{teamName}</b>}
									triggerOnHover={true}
									hideOnInsideClick={true}
									align="center"
									arrowSize={8}
									arrowColor="var(--color-gray-a)"
									triggerClassName="[&&]:inline-block">
									<div className="flex flex-row bg-gray-a border-gray-a border-4 rounded p-2 gap-4 drop-shadow-xl text-white notranslate">
										{otherStudentsOnTeams.map(({ teamName, students }, index) => (
											<Fragment key={index}>
												<div>
													<b>
														<u>{teamName}</u>
													</b>
													<ul>
														{students.map((name, index) => (
															<li key={index}>{name}</li>
														))}
													</ul>
												</div>
												{index !== otherStudentsOnTeams.length - 1 ? (
													<div className="w-0 border-l-2 border-white self-stretch" />
												) : null}
											</Fragment>
										))}
									</div>
								</Popover>
							)
						},
						textToSpeech: () => ({ text: teamName }),
					},
				},
				preparser: getSpeakingTaskPreparser({ allowStudentName: true, allowTeamName: true }),
			}
		},
		[students, teams, studentIds]
	)
}

/**
 * useMarkdownOverridesForSpeakingTaskFeedback - get any overrides which need to be used to render markdown for the literacy event feedback request
 *
 * @param {?FullStateLiteracyEventFeedbackRequest} feedbackRequest? - the feedback request being rendered
 *
 * @return {MarkdownOverridesBundle}
 */
export function useMarkdownOverridesForSpeakingTaskFeedback(
	feedbackRequest?: ?FullStateLiteracyEventFeedbackRequest
): MarkdownOverridesBundle {
	return useMarkdownOverridesForStudentIds(
		// memoized because this is used in a dependency array in another hook
		useMemo(() => (feedbackRequest ? [feedbackRequest.studentId] : null), [feedbackRequest])
	)
}

/**
 * useMarkdownOverridesForSpeakingTask - get any overrides which need to be used to render markdown for the given literacy event speaking task target
 *
 * @param {?FullStateLiteracyEventFeedbackRequest} feedbackRequest? - the feedback request being rendered
 *
 * @return {MarkdownOverridesBundle}
 */
export function useMarkdownOverridesForSpeakingTask(
	speakingTask?: ?$PropertyType<FullStateLiteracyEventStudentTaskStateSpeaking, 'talkTo'>
): MarkdownOverridesBundle {
	const currentStudentId = useSelector(state => getStudentId(state.general))

	// memoized because this is used in a dependency array in another hook
	const studentIds = useMemo(() => {
		if (!speakingTask) {
			if (currentStudentId) {
				return [currentStudentId]
			}
		} else if (
			speakingTask.type === LITERACY_EVENT.TASK.SPEAKING_TASK_DISCRIMINATORS.TARGET.STUDENT
		) {
			return speakingTask.studentIds
		}
		return []
	}, [currentStudentId, speakingTask])

	return useMarkdownOverridesForStudentIds(studentIds)
}
